import { Injectable } from "@angular/core";
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from "@angular/material/legacy-dialog";
import { pushError } from "@app/_helpers/globalErrorHandler";
import {
	EvaluateWorkingHoursOptions,
	evaluateWorkingHours,
	type WorkingHourViolation,
} from "@app/_helpers/rulebreakUtil";
import { coerceTimeFormat, getActiveSchedule } from "@app/_helpers/utils";
import {
	ComeAndGoSuggestionDialogComponent,
	PROCEED_WITH_VIOLATION,
	type SuggestionComegoTime,
	WorkingHoursUpdateData,
} from "@app/components/come-and-go-suggestion-dialog/come-and-go-suggestion-dialog.component";
import {
	buildDesiredWorkingHours,
	buildIncrementalWorkingHours,
	buildMinimalReplaceChangeSet,
	buildPauseChangeSet,
	buildProjectedDayForValidation,
	buildScheduleBasedWorkingHours,
	changeSetHasWrites,
	type DesiredWorkingHourEntry,
	dayEqualsDesired,
	expectedNormalizedDurationMs,
	isDurationProjectTime,
	type ScheduleWorkPeriod,
	type WorkingHourChangeSet,
} from "@app/services/mixed-times-working-hours.calc";
import { Order } from "@datorama/akita";
import { environment } from "@env/environment";
import {
	ComegoQuery,
	ComegoService,
	ComegoStore,
	ComegoTime,
	ComegoTimeType,
	MyTimesQuery,
	MyTimesService,
	Time,
	UserSettings,
	UserSettingsQuery,
	WorkingHourSettings as WHSettings,
} from "@timeghost/web-api";
import { MyTimesStore } from "@timeghost/web-api/lib/stores/myTimes/myTimes.store";
import {
	addDays,
	addMilliseconds,
	differenceInMilliseconds,
	endOfDay,
	format,
	isSameDay,
	isValid,
	startOfDay,
} from "date-fns/esm";
import { firstValueFrom } from "rxjs";

export type ProjectTimeDeleteResult = { deleted: true; before: { start: Date; end: Date } };
export type ProjectTimeDialogResult = Time | Time[] | ProjectTimeDeleteResult | null | undefined;

/**
 * Not-yet-persisted project time as built by create call sites: no id, and
 * start/end may still be Date objects instead of ISO strings.
 */
export type ProjectTimeDraft = Partial<Record<keyof Time, unknown>> & {
	start: string | Date;
	end?: string | Date | null;
};

type DayProjection = {
	projectedDay: ComegoTime[];
	idsToDelete: string[];
	entriesToAdd: Array<{ name?: string; start: Date; end: Date; type: ComegoTimeType }>;
	entriesToUpdate: Array<{ id: string; name?: string; start: Date; end: Date; type: ComegoTimeType }>;
};

type SyncDayOptions = {
	includeTime?: Time;
	excludeProjectTimeId?: string;
	excludeProjectTime?: Time;
	/**
	 * Pre-create gate: when there is no rulebreak dialog, stash the WH change set
	 * instead of persisting so the caller can create PH first.
	 */
	deferNoViolationPersist?: boolean;
};

type SyncDayOutcome = "noop" | "persisted" | "deferred" | "cancelled" | "dialog_resolved" | "obsolete";

/** Read-only projection of a day plus the rule verdict on it, shared by sync and the undo re-check. */
type DayEvaluation = {
	dayStart: Date;
	projectTimes: Time[];
	existing: ComegoTime[];
	desired: DesiredWorkingHourEntry[];
	dayForValidation: ComegoTime[];
	changeSet: WorkingHourChangeSet;
	wh?: WorkingHourRules;
	prevDayItem: ComegoTime | null;
	nextDayItem: ComegoTime | null;
	restWindow: { earliestStart: Date | null; latestEnd: Date | null };
	/** Every rule violation the projected day carries (both pre-existing and newly introduced). */
	violations: WorkingHourViolation[];
	/** Violations the projection introduces that the persisted day did not already have. */
	newlyIntroduced: WorkingHourViolation[];
	existingViolationCount: number;
	dayMatchesDesired: boolean;
};

/** The API type omits the break-credit threshold the rules are evaluated with. */
type WorkingHourRules = WHSettings & { minimumBreakDurationForCredit?: number };

/**
 * Identifies a violation for "was this already there before the change?".
 * The type alone is too coarse for rest: it is reported once against the previous day and once
 * against the next day, and keying on the type let a pre-existing one on either side hide a newly
 * introduced one on the other. `date` is the previous day for rest_prev and the current day
 * everywhere else, which separates the two sides without splitting break/maxHours.
 */
const violationKey = (violation: WorkingHourViolation): string => `${violation.type}:${violation.date}`;

/**
 * Close result for a correction dialog that an undo made pointless: the conflict it was raised for
 * no longer exists, so nothing is written and the undo's own sync rebuilds the day.
 */
const CORRECTION_OBSOLETE = Symbol("CORRECTION_OBSOLETE");

/**
 * Violations the user already accepted a rule fix for, so later edits that break the same rule
 * are fixed without asking again. Scoped per violation, not per day: a fix accepted for one rule
 * says nothing about a rule the user has never been shown.
 */
const AUTO_FIX_STORAGE_KEY = "tg:wh-autofix";
const AUTO_FIX_RETENTION_MS = 90 * 24 * 3600 * 1000;
/** Separates the day scope from the violation identity; entries without it predate per-violation consent. */
const AUTO_FIX_KEY_SEPARATOR = "|";

@Injectable({
	providedIn: "root",
})
export class MixedTimesSyncService {
	/** Calculated day snapshots keyed by userId:dayISO for validation before persist. */
	private readonly calculatedDayCache = new Map<string, ComegoTime[]>();
	/** WH change sets deferred until after PH create succeeds (confirm → finalize). */
	private readonly pendingChangeSetByKey = new Map<string, WorkingHourChangeSet>();
	/** Keys whose WH was already decided by a pre-create confirm, so finalize must not re-sync. */
	private readonly confirmedKeys = new Set<string>();
	/** Keys whose confirm→finalize cycle already completed; a second finalize is a no-op. */
	private readonly finalizedKeys = new Set<string>();
	/** Serializes sync per user to avoid concurrent overwrites. */
	private readonly syncTailByUser = new Map<string, Promise<unknown>>();
	/** In-flight day-window fetches, keyed userId:dayISO; dropped again once settled. */
	private readonly dayLoadByKey = new Map<string, Promise<void>>();
	/** Correction dialogs currently on screen, keyed userId:dayISO, with the sync that raised them. */
	private readonly openCorrectionDialogs = new Map<
		string,
		{
			ref: MatDialogRef<ComeAndGoSuggestionDialogComponent>;
			userId: string;
			day: Date;
			options: SyncDayOptions;
		}
	>();

	constructor(
		private comegoService: ComegoService,
		private comegoQuery: ComegoQuery,
		private myTimesQuery: MyTimesQuery,
		private myTimesService: MyTimesService,
		private userSettingsQuery: UserSettingsQuery,
		private dialog: MatDialog
	) {}

	private snapshotTime(entry: Time): Time {
		return structuredClone(entry);
	}

	private resolveTargetUserId(time?: Time | null): string {
		return time?.user?.id ?? this.userSettingsQuery.getValue().id;
	}

	private resolveWorkspaceUser(userId: string) {
		const currentUser = this.userSettingsQuery.getValue();
		return currentUser.workspace?.users?.find((u) => u.id === userId) ?? { id: userId };
	}

	private shouldSyncDay(day: Date): boolean {
		const settings = this.userSettingsQuery.getValue().workspace?.settings;
		if (!settings?.allHoursEnabled) return false;
		const legacyTimesBefore = settings.legacyTimesBefore;
		if (!legacyTimesBefore) return true;
		const cutover = startOfDay(new Date(legacyTimesBefore));
		if (!isValid(cutover)) return true;
		return startOfDay(day).getTime() >= cutover.getTime();
	}

	private cacheKey(userId: string, day: Date): string {
		return `${userId}:${startOfDay(day).toISOString()}`;
	}

	private clearDayCache(userId: string, day: Date): void {
		this.calculatedDayCache.delete(this.cacheKey(userId, day));
	}

	private clearPendingChangeSet(userId: string, day: Date): void {
		this.pendingChangeSetByKey.delete(this.cacheKey(userId, day));
	}

	/** Live consent records: expired and pre-per-violation entries are dropped on read. */
	private readAutoFixRecords(): Record<string, number> {
		let parsed: unknown = null;
		try {
			const raw = localStorage.getItem(AUTO_FIX_STORAGE_KEY);
			parsed = raw ? JSON.parse(raw) : null;
		} catch {
			return {};
		}
		if (!parsed || typeof parsed !== "object") return {};
		const cutoff = Date.now() - AUTO_FIX_RETENTION_MS;
		return Object.fromEntries(
			Object.entries(parsed as Record<string, unknown>).filter(
				([key, at]) => key.includes(AUTO_FIX_KEY_SEPARATOR) && typeof at === "number" && at >= cutoff
			) as Array<[string, number]>
		);
	}

	private autoFixKey(userId: string, day: Date, violation: WorkingHourViolation): string {
		return `${this.cacheKey(userId, day)}${AUTO_FIX_KEY_SEPARATOR}${violationKey(violation)}`;
	}

	private writeAutoFixRecords(records: Record<string, number>): void {
		try {
			localStorage.setItem(AUTO_FIX_STORAGE_KEY, JSON.stringify(records));
		} catch {
			// Storage being unavailable only costs quiet re-applies for later edits.
		}
	}

	/** Remembers that a rule fix was accepted for exactly these violations. */
	private rememberAutoFix(userId: string, day: Date, violations: WorkingHourViolation[]): void {
		if (!violations.length) return;
		const records = this.readAutoFixRecords();
		const now = Date.now();
		for (const violation of violations) {
			records[this.autoFixKey(userId, day, violation)] = now;
		}
		this.writeAutoFixRecords(records);
	}

	/** Clears every consent for this day so a later rulebreak can show the suggestion again. */
	private forgetAutoFix(userId: string, day: Date): void {
		const prefix = `${this.cacheKey(userId, day)}${AUTO_FIX_KEY_SEPARATOR}`;
		this.writeAutoFixRecords(
			Object.fromEntries(Object.entries(this.readAutoFixRecords()).filter(([key]) => !key.startsWith(prefix)))
		);
	}

	/** True once a fix was accepted for every one of these violations (local record). */
	private hasAutoFixConsent(userId: string, day: Date, violations: WorkingHourViolation[]): boolean {
		if (!violations.length) return false;
		const records = this.readAutoFixRecords();
		return violations.every((violation) => this.autoFixKey(userId, day, violation) in records);
	}

	toProjectTimeInterval(time: Time): { start: Date; end: Date } {
		return { start: new Date(time.start), end: new Date(time.end) };
	}

	/** Overnight splits: only the earliest start day participates in WH sync. */
	private resolveStartDayBatch(result: Time | Time[] | null | undefined): Time[] {
		const times = (Array.isArray(result) ? result : result ? [result] : []).filter((t) => !!t?.start);
		if (!times.length) return [];
		const sessionStart = times.reduce((earliest, t) =>
			new Date(t.start).getTime() < new Date(earliest.start).getTime() ? t : earliest
		);
		return times.filter((t) => isSameDay(new Date(t.start), new Date(sessionStart.start)));
	}

	/** Drafts carry Date start/end and no id; the projection expects Time-shaped rows. */
	private toDraftSnapshot(draft: ProjectTimeDraft): Time {
		const start = draft.start instanceof Date ? draft.start.toISOString() : draft.start;
		const end = draft.end instanceof Date ? draft.end.toISOString() : (draft.end ?? undefined);
		return { ...draft, start, end } as unknown as Time;
	}

	/**
	 * Pre-create gate: project WH from a draft PH interval and show the rulebreak
	 * suggestion if needed. Returns false when the user cancels (caller must not
	 * send the PH create/close API). On success with no dialog, WH writes are
	 * deferred until {@link finalizeAfterProjectTimeCreate}.
	 */
	async confirmProjectTimeCreate(draft: ProjectTimeDraft | ProjectTimeDraft[] | null | undefined): Promise<boolean> {
		const drafts = (Array.isArray(draft) ? draft : draft ? [draft] : [])
			.filter((d) => !!d?.start)
			.map((d) => this.toDraftSnapshot(d));
		const startDayTimes = this.resolveStartDayBatch(drafts);
		const created = startDayTimes[0];
		if (!created?.start) return true;
		if (!this.shouldSyncDay(new Date(created.start))) return true;
		const userId = this.resolveTargetUserId(created);
		const day = startOfDay(new Date(created.start));
		const key = this.cacheKey(userId, day);
		await this.ensureDayDataLoaded(day, userId);
		const outcome = await this.enqueueSync(userId, () => {
			this.pendingChangeSetByKey.delete(key);
			this.confirmedKeys.delete(key);
			this.finalizedKeys.delete(key);
			return this.synchronizeDay(day, userId, {
				includeTime: created,
				deferNoViolationPersist: true,
			});
		});
		if (outcome === "cancelled") {
			this.pendingChangeSetByKey.delete(key);
			return false;
		}
		if (outcome === "obsolete") {
			// An undo retired the dialog instead of the user answering it, so nothing was decided
			// here. Leaving the day unconfirmed lets finalize re-sync it once the PH exists.
			this.pendingChangeSetByKey.delete(key);
			return true;
		}
		// Marks the day as decided: finalize applies the deferred change set (if any) and never re-syncs.
		this.confirmedKeys.add(key);
		return true;
	}

	/**
	 * After PH create/close succeeds, persist the WH change set deferred by
	 * {@link confirmProjectTimeCreate}. Nothing is written when the rulebreak dialog
	 * already wrote WH. Callers that skipped the confirm step still get a normal sync.
	 * Idempotent per confirm cycle so overnight stop (updateSplit + afterProjectRecordingClosed)
	 * does not open a second rulebreak dialog.
	 */
	async finalizeAfterProjectTimeCreate(result: Time | Time[] | null | undefined): Promise<void> {
		const startDayTimes = this.resolveStartDayBatch(result);
		const created = startDayTimes[0];
		if (!created?.start) return;
		if (!this.shouldSyncDay(new Date(created.start))) return;
		const userId = this.resolveTargetUserId(created);
		const day = startOfDay(new Date(created.start));
		const key = this.cacheKey(userId, day);
		await this.ensureDayDataLoaded(day, userId);
		await this.enqueueSync(userId, async () => {
			if (this.finalizedKeys.has(key)) return;
			const wasConfirmed = this.confirmedKeys.delete(key);
			const pending = this.pendingChangeSetByKey.get(key);
			this.pendingChangeSetByKey.delete(key);
			if (pending && changeSetHasWrites(pending)) {
				await this.persistChangeSet(pending, userId, day);
				this.finalizedKeys.add(key);
				return;
			}
			if (wasConfirmed) {
				this.finalizedKeys.add(key);
				return;
			}
			await this.synchronizeDay(day, userId, { includeTime: created });
			this.finalizedKeys.add(key);
		});
	}

	/**
	 * @deprecated Prefer confirmProjectTimeCreate → persist PH → finalizeAfterProjectTimeCreate.
	 * Kept as an immediate post-persist sync for non-create paths that still call it.
	 */
	handleProjectTimeCreate(result: Time | Time[] | null | undefined): Promise<void> {
		const startDayTimes = this.resolveStartDayBatch(result);
		const created = startDayTimes[0];
		if (!created?.start) return Promise.resolve();
		if (!this.shouldSyncDay(new Date(created.start))) return Promise.resolve();
		return this.handleProjectTimeCreateAsync(created);
	}

	private async handleProjectTimeCreateAsync(created: Time): Promise<void> {
		const userId = this.resolveTargetUserId(created);
		const day = startOfDay(new Date(created.start));
		await this.ensureDayDataLoaded(day, userId);
		await this.enqueueSync(userId, () => this.synchronizeDay(day, userId, { includeTime: created }));
	}

	handleProjectTimeDialogClose(result: ProjectTimeDialogResult, originalEntry: Time): Promise<void> {
		const original = this.snapshotTime(originalEntry);
		if (!original?.start) return Promise.resolve();
		return this.handleProjectTimeDialogCloseAsync(result, original);
	}

	private async handleProjectTimeDialogCloseAsync(result: ProjectTimeDialogResult, original: Time): Promise<void> {
		const userId = this.resolveTargetUserId(original);
		const originalDay = startOfDay(new Date(original.start));
		const syncOriginalDay = this.shouldSyncDay(originalDay);

		if (this.isProjectTimeDeleteResult(result)) {
			if (!syncOriginalDay) return;
			await this.ensureDayDataLoaded(originalDay, userId);
			// Autofix consent is day-scoped: only drop it when this delete empties the day.
			if (!this.getDayProjectTimes(originalDay, userId, { excludeId: original.id }).length) {
				this.forgetAutoFix(userId, originalDay);
			}
			await this.enqueueSync(userId, () =>
				this.synchronizeDay(originalDay, userId, {
					excludeProjectTimeId: original.id,
					excludeProjectTime: original,
				})
			);
			return;
		}
		if (!result) return;
		const updated = Array.isArray(result) ? result[0] : result;
		if (!updated?.start) return;

		const updatedUserId = this.resolveTargetUserId(updated);
		const updatedDay = startOfDay(new Date(updated.start));
		const syncUpdatedDay = this.shouldSyncDay(updatedDay);
		const dayOrUserMoved = !isSameDay(originalDay, updatedDay) || userId !== updatedUserId;

		// Metadata-only edits (name, project, tags, …) must not reload or rebuild WH.
		if (!dayOrUserMoved && !this.projectTimeTimesChanged(original, updated)) {
			return;
		}

		if (syncOriginalDay) {
			await this.ensureDayDataLoaded(originalDay, userId);
		}
		if (syncUpdatedDay && dayOrUserMoved) {
			await this.ensureDayDataLoaded(updatedDay, updatedUserId);
		}

		if (dayOrUserMoved) {
			if (syncOriginalDay) {
				await this.enqueueSync(userId, () =>
					this.synchronizeDay(originalDay, userId, {
						excludeProjectTimeId: original.id,
						excludeProjectTime: original,
					})
				);
			}
			if (syncUpdatedDay) {
				await this.enqueueSync(updatedUserId, () =>
					this.synchronizeDay(updatedDay, updatedUserId, { includeTime: updated })
				);
			}
			return;
		}

		if (!syncUpdatedDay) return;
		await this.enqueueSync(updatedUserId, () =>
			this.synchronizeDay(updatedDay, updatedUserId, { includeTime: updated })
		);
	}

	/** True when start/end/duration (or input mode) differ enough to change WH projection. */
	private projectTimeTimesChanged(before: Time, after: Time): boolean {
		const sameMs = (a?: string | Date | null, b?: string | Date | null) =>
			(a ? new Date(a).getTime() : null) === (b ? new Date(b).getTime() : null);

		const beforeDuration = isDurationProjectTime(before);
		const afterDuration = isDurationProjectTime(after);
		if (beforeDuration !== afterDuration) return true;
		if (beforeDuration || afterDuration) {
			return !sameMs(before.start, after.start) || (before.timeDiff ?? 0) !== (after.timeDiff ?? 0);
		}
		return !sameMs(before.start, after.start) || !sameMs(before.end, after.end);
	}

	handleProjectTimeDelete(entry: Time): Promise<void> {
		const snapshot = this.snapshotTime(entry);
		if (!snapshot?.start) return Promise.resolve();
		// A running recording never produced persisted WH: the only mirror is the paired
		// open come-go row, which the caller deletes. Rebuilding the day here rewrites
		// unrelated working hours.
		if (!snapshot.end) return Promise.resolve();
		if (!this.shouldSyncDay(new Date(snapshot.start))) return Promise.resolve();
		return this.handleProjectTimeDeleteAsync(snapshot);
	}

	private async handleProjectTimeDeleteAsync(snapshot: Time): Promise<void> {
		const userId = this.resolveTargetUserId(snapshot);
		const day = startOfDay(new Date(snapshot.start));
		await this.ensureDayDataLoaded(day, userId);
		// Autofix consent is day-scoped: only drop it when this delete empties the day.
		if (!this.getDayProjectTimes(day, userId, { excludeId: snapshot.id }).length) {
			this.forgetAutoFix(userId, day);
		}
		await this.enqueueSync(userId, () =>
			this.synchronizeDay(day, userId, {
				excludeProjectTimeId: snapshot.id,
				excludeProjectTime: snapshot,
			})
		);
	}

	/**
	 * An undo reverted project times while the correction dialog raised for their day is still open.
	 * Retires that dialog when the reverted state no longer breaks the rule it was raised for; a
	 * conflict that survives the undo leaves it up.
	 *
	 * Call this before the undo's own sync: the dialog is awaited inside the per-user sync queue, so
	 * anything enqueued first would wait for the very dialog it is meant to retire.
	 *
	 * `pendingRestore` marks the delete-undo path, where the entries are re-created only afterwards
	 * and are therefore projected in by hand instead of read from the store.
	 */
	closeCorrectionDialogIfResolved(
		affected: Time | Time[] | null | undefined,
		options: { pendingRestore?: boolean } = {}
	): void {
		if (!this.openCorrectionDialogs.size) return;
		const entries = (Array.isArray(affected) ? affected : affected ? [affected] : []).filter((t) => !!t?.start);
		if (!entries.length) return;
		const affectedIds = new Set(entries.map((t) => t.id).filter((id): id is string => !!id));

		for (const [openKey, tracked] of [...this.openCorrectionDialogs.entries()]) {
			const onDay = entries.filter(
				(t) => this.resolveTargetUserId(t) === tracked.userId && isSameDay(new Date(t.start), tracked.day)
			);
			// The undo has to touch either the dialog's day or the entry its sync was keyed on.
			if (!onDay.length && !this.optionsReferenceIds(tracked.options, affectedIds)) continue;
			// A submit is already writing this projection; pulling the dialog out would strand it.
			if (tracked.ref.componentInstance?.isLoading?.value) continue;

			// Skip rows the restore already landed: the store copy is projected in on its own.
			const restores = options.pendingRestore
				? onDay.filter((t) => !!t.end && !this.myTimesQuery.getEntity(t.id))
				: [];
			const evaluation = this.evaluateDay(
				tracked.day,
				tracked.userId,
				this.optionsAfterUndo(tracked.options, affectedIds),
				restores
			);
			const resolved =
				evaluation.dayMatchesDesired ||
				!changeSetHasWrites(evaluation.changeSet) ||
				evaluation.newlyIntroduced.length === 0;
			if (!resolved) continue;

			// Untracked before the close settles, so a second undo entry cannot close it again.
			this.openCorrectionDialogs.delete(openKey);
			tracked.ref.close(CORRECTION_OBSOLETE);
		}
	}

	private optionsReferenceIds(options: SyncDayOptions, ids: Set<string>): boolean {
		return (
			(!!options.includeTime?.id && ids.has(options.includeTime.id)) ||
			(!!options.excludeProjectTimeId && ids.has(options.excludeProjectTimeId))
		);
	}

	/**
	 * The include/exclude the dialog's sync was built with, minus the parts the undo invalidated:
	 * an included snapshot of an entry that has since been reverted, and an exclusion of an entry
	 * that is coming back. Everything else is re-read from the store.
	 */
	private optionsAfterUndo(options: SyncDayOptions, affectedIds: Set<string>): SyncDayOptions {
		const includeStale = !!options.includeTime?.id && affectedIds.has(options.includeTime.id);
		const excludeStale = !!options.excludeProjectTimeId && affectedIds.has(options.excludeProjectTimeId);
		return {
			includeTime: includeStale ? undefined : options.includeTime,
			excludeProjectTimeId: excludeStale ? undefined : options.excludeProjectTimeId,
			excludeProjectTime: excludeStale ? undefined : options.excludeProjectTime,
		};
	}

	private isProjectTimeDeleteResult(result: ProjectTimeDialogResult): result is ProjectTimeDeleteResult {
		return (
			!!result &&
			typeof result === "object" &&
			!Array.isArray(result) &&
			(result as ProjectTimeDeleteResult).deleted === true &&
			!!(result as ProjectTimeDeleteResult).before?.start &&
			!!(result as ProjectTimeDeleteResult).before?.end
		);
	}

	async updateWorkinghoursFromProjecthours(projectTimes: Time[]) {
		if (!projectTimes?.length) return;
		const projectTime = projectTimes[0];
		if (!projectTime?.start) return;
		const day = startOfDay(new Date(projectTime.start));
		if (!this.shouldSyncDay(day)) return;
		const userId = this.resolveTargetUserId(projectTime);
		await this.ensureDayDataLoaded(day, userId);
		await this.enqueueSync(userId, () => this.synchronizeDay(day, userId, { includeTime: projectTime }));
	}

	private enqueueSync<T>(userId: string, task: () => Promise<T>): Promise<T> {
		const prev = this.syncTailByUser.get(userId) ?? Promise.resolve();
		const next = prev.catch((): undefined => undefined).then(task);
		this.syncTailByUser.set(
			userId,
			next
				.catch((): undefined => undefined)
				.then(() => {
					if (this.syncTailByUser.get(userId) === next) {
						this.syncTailByUser.delete(userId);
					}
				})
		);
		return next;
	}

	private getScheduleDayStart(day: Date, userId?: string): Date | null {
		const periods = this.getScheduleDayPeriods(day, userId);
		if (!periods?.length) return null;
		const starts = periods
			.map((period) => coerceTimeFormat(period.from, day))
			.filter((date): date is Date => !!date)
			.sort((a, b) => a.getTime() - b.getTime());
		return starts[0] ?? null;
	}

	private getScheduleDayPeriods(day: Date, userId?: string): ScheduleWorkPeriod[] | null {
		const currentUser = this.userSettingsQuery.getValue();
		const targetUserId = userId ?? currentUser.id;
		const scheduleUser = { ...currentUser, id: targetUserId } as UserSettings;
		const schedule = getActiveSchedule(scheduleUser, day);
		const dayItem = schedule?.items?.find((item) => item.enabled && item.day === startOfDay(day).getUTCDay());
		if (!dayItem?.enabled || !dayItem.items?.length) return null;
		const periods = dayItem.items
			.map((item) => ({
				from: item.from ?? (item as { start?: string }).start,
				to: item.to ?? (item as { end?: string }).end,
			}))
			.filter(
				(period): period is ScheduleWorkPeriod =>
					typeof period.from === "string" &&
					period.from.length > 0 &&
					typeof period.to === "string" &&
					period.to.length > 0
			);
		return periods.length ? periods : null;
	}

	private getDayProjectTimes(
		day: Date,
		userId: string,
		options?: { includeTime?: Time; excludeId?: string }
	): Time[] {
		const times = this.myTimesQuery.getAll({
			filterBy: (t) =>
				t.user?.id === userId &&
				!!t.start &&
				!!t.end && // closed only — open recordings are dual-started, not unioned
				isSameDay(new Date(t.start), day) &&
				(!options?.excludeId || t.id !== options.excludeId),
		});
		const include = options?.includeTime;
		// Include drafts (no id) and closed updates not yet in the store (e.g. pre-create gate).
		const isExcluded = !!options?.excludeId && include?.id === options.excludeId;
		if (include?.end && !isExcluded) {
			const alreadyPresent = !!include.id && times.some((t) => t.id === include.id);
			if (!alreadyPresent) {
				return [...times, include];
			}
		}
		return times;
	}

	getWorkinghoursForDay(day: Date, userId?: string) {
		const targetUserId = userId ?? this.userSettingsQuery.getValue().id;
		return this.comegoQuery.getAll({
			filterBy: (x) => isSameDay(new Date(x.start), day) && x.user?.id === targetUserId,
			sortBy: (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
		});
	}

	/**
	 * Loads the day window once per user and day. The current user's working hours are not
	 * guaranteed to be in the store either: pages that only show project times never fetch
	 * come-and-go, and projecting against a half-loaded day recreates rows that already exist
	 * on the server (overlap errors) or deletes rows that were never loaded.
	 */
	private ensureDayDataLoaded(day: Date, userId: string): Promise<void> {
		const key = this.cacheKey(userId, day);
		const pending = this.dayLoadByKey.get(key);
		if (pending) return pending;
		const load = this.loadDayData(day, userId).catch((err) => {
			pushError(err);
		});
		this.dayLoadByKey.set(key, load);
		// Only concurrent callers share a load. A full-day replace deletes exactly what the store
		// holds, so every sync has to re-read the server state instead of trusting a snapshot taken
		// earlier in the session (rows added meanwhile would survive and collide with the creates).
		void load.finally(() => {
			if (this.dayLoadByKey.get(key) === load) this.dayLoadByKey.delete(key);
		});
		return load;
	}

	private async loadDayData(day: Date, userId: string): Promise<void> {
		const rangeStart = startOfDay(addDays(day, -1));
		const rangeEnd = endOfDay(addDays(day, 1));

		const comego = await this.comegoService.getBetween(rangeStart, rangeEnd, userId);
		if (comego?.length) {
			(this.comegoQuery.__store__ as ComegoStore).upsertMany(comego);
		}

		const dayStart = startOfDay(day);
		const dayEnd = endOfDay(day);
		const times = await firstValueFrom(
			this.myTimesService.adapter.get<Time[]>(
				environment.serverUrl +
					`/get/times?$filter=start ge '${dayStart.toISOString()}' and start le '${dayEnd.toISOString()}' and user__id eq '${userId}'`
			)
		);
		if (times?.length) {
			(this.myTimesQuery.__store__ as MyTimesStore).upsertMany(times);
		}
	}

	/**
	 * Projects the day's working hours from the PH union (+ duration append) and runs the workspace
	 * rules over the result. Pure read: no store writes and no cache updates, so the undo re-check
	 * can reuse it to ask whether a conflict still exists. `extraProjectTimes` covers rows a caller
	 * knows about but the store does not hold yet (an undo that is about to restore a deletion).
	 */
	private evaluateDay(
		day: Date,
		userId: string,
		options: SyncDayOptions = {},
		extraProjectTimes: Time[] = []
	): DayEvaluation {
		const dayStart = startOfDay(day);
		const projectTimes = [
			...this.getDayProjectTimes(dayStart, userId, {
				includeTime: options.includeTime,
				excludeId: options.excludeProjectTimeId,
			}),
			...extraProjectTimes,
		];

		const user = this.userSettingsQuery.getValue();
		const wh = user.workspace?.schedules?.workspace?.workinghours as WorkingHourRules | undefined;
		const prevDayItem = this.getAdjacentDayWorkBoundary(dayStart, -1, userId);
		const nextDayItem = this.getAdjacentDayWorkBoundary(dayStart, 1, userId);
		const restWindow = this.buildRestWindow(prevDayItem, nextDayItem, wh?.breakBetweenWorkingDays);

		// Working hours mirror the project-hour union as-is. Pre-shifting the timeline (to keep a
		// rest-compliant day start) invented clock times that no longer matched any recording, so a
		// rest problem now has to surface as a violation the dialog can suggest a fix for. Only the
		// start of a duration-only day, which has no recorded clock times at all, follows the rule.
		const desired = buildDesiredWorkingHours({
			day: dayStart,
			projectTimes,
			scheduleDayStart: this.getScheduleDayStart(dayStart, userId),
			earliestStart: restWindow.earliestStart,
		});
		// Open rows (running check-in, dual-started WH of the timer being stopped) are not settled
		// working hours: evaluateWorkingHours treats them as work until now, which makes the
		// violation the project time introduces look pre-existing and suppresses the dialog.
		// The dialog's full-day recreate still deletes them via the live store day.
		const existing = this.getWorkinghoursForDay(dayStart, userId).filter((e) => !!e.end);

		// Gaps between work become pauses before rule evaluation so they count as breaks
		// (otherwise continuous work is overcounted and a false rulebreak dialog opens).
		const dayForValidation = buildProjectedDayForValidation(desired, existing);
		const changeSet = buildMinimalReplaceChangeSet(existing, desired);

		let violations: WorkingHourViolation[] = [];
		let newlyIntroduced: WorkingHourViolation[] = [];
		let existingViolationCount = 0;
		if (wh) {
			const adjacent = { prevDayItem, nextDayItem } as EvaluateWorkingHoursOptions;
			violations = evaluateWorkingHours(dayForValidation, wh, dayStart, adjacent);
			const existingViolationKeys = new Set(
				evaluateWorkingHours(existing, wh, dayStart, adjacent).map(violationKey)
			);
			existingViolationCount = existingViolationKeys.size;
			newlyIntroduced = violations.filter((v) => !existingViolationKeys.has(violationKey(v)));
		}

		return {
			dayStart,
			projectTimes,
			existing,
			desired,
			dayForValidation,
			changeSet,
			wh,
			prevDayItem,
			nextDayItem,
			restWindow,
			violations,
			newlyIntroduced,
			existingViolationCount,
			dayMatchesDesired: dayEqualsDesired(existing, desired),
		};
	}

	/**
	 * Rebuilds the day's working hours from the PH union (+ duration append), validates the result
	 * against the workspace rules, then replaces only mismatched segments (delete then create).
	 */
	private async synchronizeDay(day: Date, userId: string, options: SyncDayOptions = {}): Promise<SyncDayOutcome> {
		if (!this.shouldSyncDay(day)) return "noop";
		const {
			dayStart,
			projectTimes,
			existing,
			desired,
			dayForValidation,
			changeSet,
			wh,
			prevDayItem,
			nextDayItem,
			restWindow,
			violations,
			newlyIntroduced,
			existingViolationCount,
			dayMatchesDesired,
		} = this.evaluateDay(day, userId, options);

		const key = this.cacheKey(userId, dayStart);
		this.calculatedDayCache.set(key, dayForValidation);

		// A break violation is offered a fix even when it already existed on the persisted day.
		// Otherwise a later edit (or an edit that leaves the merged blocks unchanged) silently
		// re-persists the violating mirror and only the timeline warning is left, with no way to
		// correct it. Other violation types keep the "only when newly introduced" behavior, so
		// this runs before the noop guards, which a pre-existing violation on an unchanged day
		// would otherwise short-circuit.
		const breakViolations = violations.filter((v) => v.type === "break");
		if (wh && (newlyIntroduced.length > 0 || breakViolations.length > 0)) {
			const triggeredByNew = newlyIntroduced.length > 0;
			// A day whose own entries are already correct is fixed by fitting the added work
			// around them; only a day with nothing valid to build on is rebuilt from the schedule.
			const suggestedWork =
				(existingViolationCount === 0
					? buildIncrementalWorkingHours({
							day: dayStart,
							desiredWork: desired,
							existingDay: existing,
							dailyMaxHours: wh.dailyMaxHours,
							breaks: wh.breaks,
							minimumBreakDurationForCredit: wh.minimumBreakDurationForCredit,
							earliestStart: restWindow.earliestStart,
							latestEnd: restWindow.latestEnd,
						})
					: null) ??
				buildScheduleBasedWorkingHours({
					day: dayStart,
					projectTimes,
					schedulePeriods: this.getScheduleDayPeriods(dayStart, userId),
					dailyMaxHours: wh.dailyMaxHours,
					earliestStart: restWindow.earliestStart,
					latestEnd: restWindow.latestEnd,
				});
			const suggestedChangeSet = buildMinimalReplaceChangeSet(existing, suggestedWork);
			const suggestedDay = buildProjectedDayForValidation(suggestedWork, existing);

			// A newly introduced violation always opens the dialog so the user sees what their edit
			// caused. A pre-existing break violation is only worth stepping in for when the
			// suggestion is a real, break-compliant improvement over the persisted day; otherwise
			// re-opening the dialog on every edit would be noise (and could loop).
			const adjacent = { prevDayItem, nextDayItem } as EvaluateWorkingHoursOptions;
			const suggestionFixesBreak =
				!dayEqualsDesired(existing, suggestedWork) &&
				evaluateWorkingHours(suggestedDay, wh, dayStart, adjacent).every((v) => v.type !== "break");

			if (triggeredByNew || suggestionFixesBreak) {
				// When the day already carried the break violation there is nothing "newly
				// introduced" to show, so surface the current break violations instead.
				const dialogViolations = triggeredByNew ? newlyIntroduced : breakViolations;
				let suggested = {
					...this.changeSetToDayProjection(suggestedDay, suggestedChangeSet),
					...this.projectionFromSuggested(suggestedDay, changeSet),
					projectedDay: suggestedDay,
					// Full-day recreate: also delete rows the change set would have updated in place.
					idsToDelete: [
						...new Set([
							...suggestedChangeSet.delete.map((d) => d.id),
							...changeSet.delete.map((d) => d.id),
							...changeSet.update.map((u) => u.id),
						]),
					],
				};

				// A day that was fixed once never mirrors the project hours again, so this branch is
				// reached on every later edit. Asking again each time is noise: re-apply the same fix.
				// Consent covers only the violations it was given for, so a rule the user has not
				// been shown on this day still opens the dialog.
				if (this.hasAutoFixConsent(userId, dayStart, dialogViolations)) {
					const quietWork = this.workEntriesOf(suggested.projectedDay);
					if (dayEqualsDesired(existing, quietWork)) {
						this.clearDayCache(userId, dayStart);
						this.clearPendingChangeSet(userId, dayStart);
						return "noop";
					}
					const quiet = buildMinimalReplaceChangeSet(existing, quietWork);
					if (options.deferNoViolationPersist) {
						this.pendingChangeSetByKey.set(key, quiet);
						this.clearDayCache(userId, dayStart);
						return "deferred";
					}
					this.clearPendingChangeSet(userId, dayStart);
					await this.persistChangeSet(quiet, userId, dayStart);
					this.rememberAutoFix(userId, dayStart, dialogViolations);
					return "persisted";
				}

				const result = await this.openRulebreakDialogForProjection(
					dayStart,
					suggested,
					dialogViolations,
					userId,
					existing,
					dayForValidation,
					options.includeTime,
					projectTimes,
					this.buildRestSummary(
						dayStart,
						dialogViolations,
						dayForValidation,
						prevDayItem,
						nextDayItem,
						wh.breakBetweenWorkingDays
					),
					options
				);
				// Dialog path always clears deferred stash — WH is either written now or aborted.
				this.clearPendingChangeSet(userId, dayStart);
				if (result === PROCEED_WITH_VIOLATION) {
					await this.persistChangeSet(changeSet, userId, dayStart);
					return "persisted";
				}
				if (result === CORRECTION_OBSOLETE) {
					// An undo already removed the conflict. Its own sync is queued behind this task
					// and rebuilds the day, so writing the now-stale change set here would fight it.
					this.clearDayCache(userId, dayStart);
					return "obsolete";
				}
				if (result == null) {
					this.clearDayCache(userId, dayStart);
					return "cancelled";
				}
				// suggestion accepted; dialog already wrote WH. Remembering the violations it was
				// shown for keeps later edits quiet even for fixes that only move work and leave no
				// marked break behind.
				this.rememberAutoFix(userId, dayStart, dialogViolations);
				this.clearDayCache(userId, dayStart);
				return "dialog_resolved";
			}
		}

		if (dayMatchesDesired) {
			this.clearDayCache(userId, dayStart);
			this.clearPendingChangeSet(userId, dayStart);
			return "noop";
		}

		if (!changeSetHasWrites(changeSet)) {
			this.clearDayCache(userId, dayStart);
			this.clearPendingChangeSet(userId, dayStart);
			return "noop";
		}

		if (options.deferNoViolationPersist) {
			this.pendingChangeSetByKey.set(key, changeSet);
			this.clearDayCache(userId, dayStart);
			return "deferred";
		}

		await this.persistChangeSet(changeSet, userId, dayStart);
		return "persisted";
	}

	private changeSetToDayProjection(projectedDay: ComegoTime[], changeSet: WorkingHourChangeSet): DayProjection {
		return {
			projectedDay,
			idsToDelete: changeSet.delete.map((d) => d.id),
			entriesToAdd: changeSet.create.map((c) => ({
				start: c.start,
				end: c.end,
				type: c.type,
			})),
			entriesToUpdate: changeSet.update.map((u) => ({
				id: u.id,
				name: u.name ?? undefined,
				start: u.start,
				end: u.end,
				type: u.type,
			})),
		};
	}

	private projectionFromSuggested(projectedDay: ComegoTime[], seed: WorkingHourChangeSet): DayProjection {
		return {
			projectedDay,
			idsToDelete: seed.delete.map((d) => d.id),
			entriesToAdd: projectedDay
				.filter((e) => !e.id && e.end)
				.map((e) => ({
					name: e.name ?? undefined,
					start: new Date(e.start),
					end: new Date(e.end!),
					type: e.type as ComegoTimeType,
				})),
			entriesToUpdate: seed.update.map((u) => ({
				id: u.id,
				name: u.name ?? undefined,
				start: u.start,
				end: u.end,
				type: u.type,
			})),
		};
	}

	private async persistChangeSet(changeSet: WorkingHourChangeSet, userId: string, day: Date): Promise<void> {
		const workspaceUser = this.resolveWorkspaceUser(userId);
		const toUpdatePayload = (entry: WorkingHourChangeSet["update"][number]): ComegoTime => {
			const existing = this.comegoQuery.getEntity(entry.id);
			return {
				...(existing ?? {}),
				id: entry.id,
				name: entry.name ?? existing?.name ?? null,
				start: entry.start.toISOString(),
				end: entry.end.toISOString(),
				type: entry.type,
				user: existing?.user ?? workspaceUser,
			} as ComegoTime;
		};
		// Delete first: the API rejects any write overlapping a still-persisted row, so the day has
		// to be empty before it is re-created. A failed delete leaves the day half-emptied, so the
		// creates are skipped and the error is reported instead of writing on top of leftovers.
		try {
			if (changeSet.delete.length) {
				await this.comegoService.delete(...(changeSet.delete.map((d) => ({ id: d.id })) as any));
			}
		} catch (error) {
			this.clearDayCache(userId, day);
			pushError(error);
			console.error(error);
			return;
		}
		try {
			if (changeSet.update.length) {
				await this.comegoService.update(...(changeSet.update.map(toUpdatePayload) as ComegoTime[]));
			}
			if (changeSet.create.length) {
				await this.comegoService.add(
					...(changeSet.create.map((entry) => ({
						// Absences are re-created verbatim, so their name and absence type ride along.
						...(entry.source ?? {}),
						start: entry.start,
						end: entry.end,
						type: entry.type,
						user: workspaceUser,
					})) as any[])
				);
			}
			this.clearDayCache(userId, day);
		} catch (error) {
			this.clearDayCache(userId, day);
			pushError(error);
			console.error(error);
		}
	}

	private getAdjacentDayWorkBoundary(day: Date, offsetDays: number, userId?: string): ComegoTime | null {
		const targetUserId = userId ?? this.userSettingsQuery.getValue().id;
		const adjacentDay = addDays(day, offsetDays);
		if (!isValid(adjacentDay)) return null;
		const entries = this.comegoQuery.getAll({
			filterBy: (x) =>
				isSameDay(new Date(x.start), adjacentDay) &&
				x.user?.id === targetUserId &&
				(x.type === "work" || x.type === "absence"),
			sortBy: (a, b) => {
				if (offsetDays < 0) {
					return new Date(b.end || 0).getTime() - new Date(a.end || 0).getTime();
				}
				return new Date(a.start).getTime() - new Date(b.start).getTime();
			},
			sortByOrder: offsetDays < 0 ? Order.DESC : Order.ASC,
			limitTo: 1,
		});
		const entry = entries?.[0];
		if (!entry) return null;
		return {
			...entry,
			start: new Date(entry.start) as any,
			end: new Date(entry.end || new Date()) as any,
		};
	}

	/** Clock bounds the rest rule leaves for a day, derived from the adjacent days' work boundaries. */
	private buildRestWindow(
		prevDayItem: ComegoTime | null,
		nextDayItem: ComegoTime | null,
		breakBetweenWorkingDays?: number | null
	): { earliestStart: Date | null; latestEnd: Date | null } {
		const restMs = (breakBetweenWorkingDays ?? 0) * 3600 * 1000;
		if (restMs <= 0) return { earliestStart: null, latestEnd: null };
		const prevEnd = prevDayItem?.end ? new Date(prevDayItem.end) : null;
		const nextStart = nextDayItem?.start ? new Date(nextDayItem.start) : null;
		return {
			earliestStart: prevEnd && isValid(prevEnd) ? addMilliseconds(prevEnd, restMs) : null,
			latestEnd: nextStart && isValid(nextStart) ? addMilliseconds(nextStart, -restMs) : null,
		};
	}

	private formatNoticeClock(
		value: string | Date | null | undefined,
		use24h: boolean,
		includeDate = false
	): string | null {
		if (value == null || value === "") return null;
		const date = value instanceof Date ? value : new Date(value);
		if (!isValid(date)) return null;
		const time = format(date, use24h ? "HH:mm" : "h:mm a");
		if (!includeDate) return time;
		// Same date token the formatDate pipe uses (user preference, default PP).
		const dateFormat = this.userSettingsQuery.getValue().settings?.dateFormat ?? "PP";
		return `${format(date, dateFormat)} ${time}`;
	}

	private buildViolationNotices(
		violations: WorkingHourViolation[],
		dayForValidation: ComegoTime[],
		includeTime?: Time | null
	): Array<{ message: string; args?: Record<string, unknown>; start: string; end: string }> {
		const use24h = !!this.userSettingsQuery.getValue().settings?.timeFormat24h;
		const notices: Array<{ message: string; args?: Record<string, unknown>; start: string; end: string }> = [];
		const seen = new Set<string>();
		for (const v of violations) {
			const key = `${v.message}:${JSON.stringify(v.args ?? {})}`;
			if (seen.has(key)) continue;
			const fromInclude = includeTime
				? {
						start: this.formatNoticeClock(includeTime.start, use24h),
						end: this.formatNoticeClock(includeTime.end, use24h),
					}
				: null;
			const fromEntry = v.entryID ? dayForValidation.find((e) => e.id === v.entryID) : undefined;
			const start = fromInclude?.start ?? this.formatNoticeClock(fromEntry?.start, use24h);
			const end = fromInclude?.end ?? this.formatNoticeClock(fromEntry?.end, use24h);
			if (!start || !end) continue;
			seen.add(key);
			notices.push({ message: v.message, args: v.args, start, end });
		}
		return notices;
	}

	/**
	 * Project-time durations behind the suggestion, in seconds (parseMsAsDuration takes seconds).
	 * The added part is derived from the union so time overlapping an existing entry is not counted twice.
	 */
	private buildProjectTimeSummary(
		day: Date,
		projectTimes: Time[],
		includeTime?: Time | null
	): WorkingHoursUpdateData["projectTimeData"] {
		const totalMs = expectedNormalizedDurationMs(projectTimes, day);
		const withoutIncluded = includeTime
			? projectTimes.filter((t) => t !== includeTime && (!includeTime.id || t.id !== includeTime.id))
			: projectTimes;
		const existingMs = includeTime ? expectedNormalizedDurationMs(withoutIncluded, day) : totalMs;
		const totalBreakMS =
			projectTimes.reduce((acc, current, index, times) => {
				if (index === times.length - 1) return acc;
				const currentEnd = new Date(current.end);
				const nextStart = new Date(times[index + 1].start);
				acc = differenceInMilliseconds(nextStart, currentEnd);
				return acc;
			}, 0) / 1000;
		return {
			existingDuration: Math.max(0, existingMs) / 1000,
			addDuration: Math.max(0, totalMs - existingMs) / 1000,
			totalDuration: Math.max(0, totalMs) / 1000,
			totalBreakDuration: totalBreakMS,
		};
	}

	/**
	 * Rest-rule figures behind the suggestion: the adjacent day's work boundary, this day's own
	 * boundary facing it, and how much rest that leaves against what the rule requires (seconds).
	 * Returns undefined when no rest rule was broken, which keeps the dialog on its default panel.
	 */
	private buildRestSummary(
		day: Date,
		violations: WorkingHourViolation[],
		dayForValidation: ComegoTime[],
		prevDayItem: ComegoTime | null,
		nextDayItem: ComegoTime | null,
		breakBetweenWorkingDays?: number | null
	): WorkingHoursUpdateData["restData"] {
		const requiredHours = breakBetweenWorkingDays ?? 0;
		if (requiredHours <= 0) return undefined;
		const restViolation = violations.find((v) => v.type === "rest");
		if (!restViolation) return undefined;

		const work = dayForValidation
			.filter((e) => e.type === "work" && !!e.end)
			.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
		if (!work.length) return undefined;

		// evaluateWorkingHours stamps the previous day on the rest check against it and the current
		// day on the one against the next day, which is the only thing separating the two sides.
		const isPrevSide = new Date(restViolation.date).getTime() < startOfDay(day).getTime();
		const adjacentBoundary = isPrevSide ? prevDayItem?.end : nextDayItem?.start;
		const ownBoundary = isPrevSide ? work[0].start : work[work.length - 1].end;
		if (!adjacentBoundary || !ownBoundary) return undefined;

		const adjacentMs = new Date(adjacentBoundary).getTime();
		const ownMs = new Date(ownBoundary).getTime();
		const availableMs = isPrevSide ? ownMs - adjacentMs : adjacentMs - ownMs;

		const use24h = !!this.userSettingsQuery.getValue().settings?.timeFormat24h;
		// Adjacent day is a different calendar day, so include the date; this day's entry stays time-only.
		const adjacentClock = this.formatNoticeClock(adjacentBoundary, use24h, true);
		const entryClock = this.formatNoticeClock(ownBoundary, use24h);
		if (!adjacentClock || !entryClock) return undefined;

		return {
			side: isPrevSide ? "prev" : "next",
			adjacentClock,
			entryClock,
			availableRestDuration: Math.max(0, availableMs) / 1000,
			requiredRestDuration: requiredHours * 3600,
		};
	}

	private async openRulebreakDialogForProjection(
		day: Date,
		cache: DayProjection,
		violations: WorkingHourViolation[],
		userId?: string,
		existingDayEntries?: ComegoTime[],
		dayForValidation: ComegoTime[] = [],
		includeTime?: Time | null,
		projectTimes: Time[] = [],
		restData?: WorkingHoursUpdateData["restData"],
		syncOptions: SyncDayOptions = {}
	): Promise<unknown> {
		const userSettings = this.userSettingsQuery.getValue();
		const targetUserId = userId ?? userSettings.id;
		const workspaceUser = this.resolveWorkspaceUser(targetUserId);
		const existingDay = existingDayEntries ?? this.getWorkinghoursForDay(day, targetUserId);
		const storeDay = this.getWorkinghoursForDay(day, targetUserId);
		// Suggestion is always a full-day recreate: strip ids so submit cannot update
		// in place over leftovers, and delete every current row before add. Absences are included
		// because the projection may have clipped them around the new work, and re-adding a clipped
		// copy next to the original would overlap.
		const replaceIdsFrom = (entries: ComegoTime[]) => entries.filter((e) => !!e.id).map((e) => e.id!);
		const idsOnProjection = cache.projectedDay
			.map((e) => e.id)
			.filter((id): id is string => typeof id === "string" && !!id);
		const idsToDelete = [
			...new Set([
				...(cache.idsToDelete ?? []),
				...replaceIdsFrom(existingDay),
				...replaceIdsFrom(storeDay),
				...idsOnProjection,
			]),
		];
		// Ids are dropped below, so the origin has to be resolved here while the projected rows can
		// still be matched against their persisted counterparts.
		const persistedDay = [...existingDay, ...storeDay];
		const sameMs = (a?: string | Date | null, b?: string | Date | null) =>
			(a ? new Date(a).getTime() : null) === (b ? new Date(b).getTime() : null);
		const isUnchanged = (entry: ComegoTime) =>
			!!entry.id &&
			persistedDay.some(
				(p) =>
					p.id === entry.id &&
					p.type === entry.type &&
					sameMs(p.start, entry.start) &&
					sameMs(p.end, entry.end)
			);
		const projectedDay = cache.projectedDay.map((entry) => {
			const { id: _id, ...rest } = entry as ComegoTime & { id?: string };
			return {
				...rest,
				user: entry.user ?? workspaceUser,
				origin: isUnchanged(entry) ? "existing" : "added",
			} as SuggestionComegoTime;
		});
		const ref = this.dialog.open(ComeAndGoSuggestionDialogComponent, {
			data: {
				time: {
					value: projectedDay,
					date: day,
				},
				forceRecreateDay: true,
				idsToDelete,
				pendingProjectionFix: userSettings.workspace?.schedules?.workspace?.workinghours
					?.preventDeviatingWorkingHours
					? false
					: true,
				violationNotices: this.buildViolationNotices(violations, dayForValidation, includeTime),
				projectTimeData: this.buildProjectTimeSummary(day, projectTimes, includeTime),
				restData,
			} as WorkingHoursUpdateData,
			disableClose: true,
		});
		// Kept so an undo landing while the dialog is open can retire it once the conflict is gone.
		const openKey = this.cacheKey(targetUserId, day);
		const tracked = { ref, userId: targetUserId, day, options: syncOptions };
		this.openCorrectionDialogs.set(openKey, tracked);
		try {
			return await firstValueFrom(ref.afterClosed());
		} finally {
			if (this.openCorrectionDialogs.get(openKey) === tracked) {
				this.openCorrectionDialogs.delete(openKey);
			}
		}
	}

	/** Closed work rows of a projected day as the desired-work shape the calc helpers take. */
	private workEntriesOf(projectedDay: ComegoTime[]): DesiredWorkingHourEntry[] {
		return projectedDay
			.filter((e) => e.type === "work" && e.end)
			.map(
				(e) =>
					({
						start: new Date(e.start),
						end: new Date(e.end!),
						durationMs: new Date(e.end!).getTime() - new Date(e.start).getTime(),
						type: "work" as ComegoTimeType,
					}) as DesiredWorkingHourEntry
			);
	}

	async fillDayWithBreaks(day: Date) {
		if (!this.shouldSyncDay(day)) return;
		const workinghours = this.getWorkinghoursForDay(day);
		const work = workinghours
			.filter((e) => e.type === "work" && e.end)
			.map(
				(e) =>
					({
						start: new Date(e.start),
						end: new Date(e.end!),
						durationMs: new Date(e.end!).getTime() - new Date(e.start).getTime(),
						type: "work" as ComegoTimeType,
					}) as DesiredWorkingHourEntry
			);
		const projected = buildProjectedDayForValidation(work, workinghours);
		const pauseCs = buildPauseChangeSet(workinghours, projected);
		if (!pauseCs.create.length) return;
		try {
			await this.comegoService.add(
				...(pauseCs.create.map((b) => ({
					start: b.start,
					end: b.end,
					type: "pause" as ComegoTimeType,
				})) as any[])
			);
		} catch (error) {
			console.error(error);
		}
	}
}
